import classNames from 'classnames';
import {
  getISOWeek,
  isFriday,
  isSaturday,
  isSunday,
  isToday,
  isWeekend,
  isYesterday
} from 'date-fns';
import i18n from 'i18next';

import { Holidays } from '../../../redux/types';
import { RosterDay } from './types';
import { Interval } from 'components/types';

import { formatByLocale } from 'utils/date';

type CellDateClassesProps = {
  date: Date;
  isHoliday?: boolean;
  view?: 'day' | 'week' | 'month';
};

export const getCellDateClasses = (
  { date, isHoliday, view }: CellDateClassesProps,
  otherClasses: string[] = []
) =>
  classNames(
    {
      'is-yesterday': isYesterday(date),
      'is-today': isToday(date),
      'is-friday': isFriday(date),
      'is-saturday': isSaturday(date),
      'is-sunday': isSunday(date),
      'is-weekend': isWeekend(date),
      'is-holiday': isHoliday,
      'is-day-view': view === 'day',
      'is-week-view': view === 'week',
      'is-month-view': view === 'month'
    },
    ...otherClasses
  );

export const getRosterDays = (interval: Interval, holidays: Holidays) => {
  const days: RosterDay[] = [];
  const { startDate, endDate } = interval;
  const currentDate = new Date(startDate);
  while (currentDate < endDate) {
    const holiday = holidays[formatByLocale(currentDate, 'yyyy-MM-dd')];
    const isHoliday = Boolean(holiday);
    days.push({
      date: new Date(currentDate),
      day: currentDate.getDate(),
      dayName: formatByLocale(currentDate, 'EEEEEE'),
      dayNr: currentDate.getDay(),
      weekNr: getISOWeek(currentDate),
      isHoliday,
      isToday: isToday(currentDate),
      description: isHoliday ? holiday.description : i18n.t('today') || ''
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return days;
};

export const prepareRosterForPrint = (content: HTMLDivElement) => {
  const contentClone = content.cloneNode(true) as HTMLElement;
  contentClone.classList.add('printed-roster');
  const rosterBody = contentClone.querySelector('.roster-body');
  if (rosterBody) {
    const osContent = rosterBody.querySelector('.os-content');
    if (osContent) {
      rosterBody.innerHTML = '';
      rosterBody.appendChild(osContent);
    }
  }
  Array.from(
    contentClone.querySelectorAll(`
                .MuiAccordionSummary-expandIconWrapper,
                .function-color,
                .dst-icon
            `)
  ).forEach((elem) => elem.remove());
  contentClone.querySelector('.filters')?.classList.add('v-hidden');
  replaceNavWithPrintHeader(contentClone);
  mergeHeaderIntoGridTables(contentClone);
  return contentClone;
};

/**
 * Merges the standalone header table's <thead> into each grid <table>,
 * so the browser repeats date headers on every printed page via
 * `display: table-header-group`.
 */
const mergeHeaderIntoGridTables = (container: HTMLElement) => {
  const tables = Array.from(
    container.querySelectorAll('table.roster-grid')
  ) as HTMLTableElement[];

  if (tables.length < 2) return;

  const headerTable = tables[0];
  const thead = headerTable.querySelector('thead');
  if (!thead) return;

  for (let i = 1; i < tables.length; i++) {
    tables[i].insertBefore(thead.cloneNode(true), tables[i].firstChild);
  }

  const headerParent = headerTable.parentElement;
  headerTable.remove();
  if (headerParent && !headerParent.hasChildNodes()) {
    headerParent.remove();
  }
};

/**
 * Replaces the interactive navigation bar (.main-nav) with a clean
 * print header showing only the plan unit name(s) and date period.
 */
const replaceNavWithPrintHeader = (container: HTMLElement) => {
  const mainNav = container.querySelector('.main-nav');
  if (!mainNav) return;

  const chipLabels = Array.from(
    mainNav.querySelectorAll('.planunit-selector .MuiChip-label')
  )
    .map((el) => el.textContent?.trim())
    .filter(Boolean);

  const periodText =
    mainNav.querySelector('.date-interval h2')?.textContent?.trim() || '';

  const printHeader = document.createElement('div');
  printHeader.className = 'print-header-info';

  if (chipLabels.length > 0) {
    const planUnitEl = document.createElement('div');
    planUnitEl.className = 'print-plan-units';
    planUnitEl.textContent = chipLabels.join(', ');
    printHeader.appendChild(planUnitEl);
  }

  if (periodText) {
    const periodEl = document.createElement('div');
    periodEl.className = 'print-period';
    periodEl.textContent = periodText;
    printHeader.appendChild(periodEl);
  }

  mainNav.parentElement?.insertBefore(printHeader, mainNav);
  (mainNav as HTMLElement).remove();
};
